// components/gifts/GiftGridBit.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const GiftGridBit = ({ giftSets }) => {
    const [selectedOccasion, setSelectedOccasion] = useState('all');
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.2 });

    const filteredSets = selectedOccasion === 'all'
        ? giftSets
        : giftSets.filter(set => set.occasion.includes(selectedOccasion));

    const occasions = [
        { id: 'all', label: 'All Occasions' },
        { id: 'birthday', label: 'Birthday' },
        { id: 'selfcare', label: 'Self-Care' },
        { id: 'festive', label: 'Festive' },
        { id: 'premium', label: 'Premium Gifting' },
        { id: 'travel', label: 'Travel' }
    ];

    return (
        <section ref={ref} className="py-24 bg-ivory">
            <div className="container mx-auto px-6 lg:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                    className="mb-16"
                >
                    <h2 className="font-playfair text-5xl md:text-6xl text-charcoal mb-8 text-center">
                        Curated Gift Collections
                    </h2>

                    <div className="flex flex-wrap justify-center gap-4 mb-12">
                        {occasions.map((occasion) => (
                            <motion.button
                                key={occasion.id}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setSelectedOccasion(occasion.id)}
                                className={`px-6 py-3 rounded-full font-poppins transition-all duration-300 ${
                                    selectedOccasion === occasion.id
                                        ? 'bg-sage-green text-white'
                                        : 'bg-white text-charcoal border border-charcoal/20 hover:border-sage-green'
                                }`}
                            >
                                {occasion.label}
                            </motion.button>
                        ))}
                    </div>
                </motion.div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={selectedOccasion}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    >
                        {filteredSets.map((set, index) => (
                            <motion.div
                                key={set.id}
                                initial={{ opacity: 0, y: 40 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                whileHover={{
                                    y: -12,
                                    transition: { duration: 0.3 }
                                }}
                                className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-luxe"
                            >
                                <div className="relative h-64 overflow-hidden">
                                    <motion.img
                                        whileHover={{ scale: 1.05 }}
                                        transition={{ duration: 0.6 }}
                                        src={set.image}
                                        alt={set.name}
                                        className="w-full h-full object-cover"
                                    />
                                    {set.isNew && (
                                        <div className="absolute top-4 right-4 bg-champagne text-charcoal px-3 py-1 rounded-full text-sm font-poppins">
                                            New Arrival
                                        </div>
                                    )}
                                </div>

                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <h3 className="font-cormorant text-2xl text-charcoal">{set.name}</h3>
                                        <span className="font-playfair text-xl text-sage-green">${set.price}</span>
                                    </div>

                                    <p className="font-poppins text-charcoal/70 mb-6">{set.description}</p>

                                    <div className="flex flex-wrap gap-2 mb-6">
                                        {set.tags.map((tag, tagIndex) => (
                                            <span
                                                key={tagIndex}
                                                className="px-3 py-1 bg-ivory text-charcoal/70 rounded-full text-sm font-poppins"
                                            >
                        {tag}
                      </span>
                                        ))}
                                    </div>

                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="w-full py-3 bg-charcoal text-white font-poppins rounded-lg
                      hover:bg-charcoal/90 transition-all duration-300"
                                    >
                                        Explore Set
                                    </motion.button>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </AnimatePresence>
            </div>
        </section>
    );
};

export default GiftGridBit;